import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  Megaphone, PaperPlaneTilt, CheckCircle, Eye, Clock, Warning,
  CaretDown, CaretUp, Users
} from "@phosphor-icons/react";

export default function BriefingsPage() {
  const { user } = useAuth();
  const [briefings, setBriefings] = useState([]);
  const [teams, setTeams] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ subject: "", message: "", team_id: "", priority: "normal", deadline: "" });
  const isLeader = user?.role === "admin" || user?.role === "team_leader";

  const load = useCallback(async () => {
    try {
      const [bRes, tRes] = await Promise.all([
        api.get("/briefings"),
        api.get("/teams/dropdown"),
      ]);
      setBriefings(bRes.data.items || []);
      setTeams(tRes.data || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async () => {
    if (!form.subject.trim() || !form.message.trim()) { toast.error("Subject and message required"); return; }
    try {
      await api.post("/briefings", form);
      toast.success("Briefing sent");
      setShowCreate(false);
      setForm({ subject: "", message: "", team_id: "", priority: "normal", deadline: "" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to send"); }
  };

  const acknowledge = async (id) => {
    try { await api.post(`/briefings/${id}/acknowledge`); toast.success("Acknowledged"); load(); }
    catch { toast.error("Failed"); }
  };

  const complete = async (id) => {
    try { await api.post(`/briefings/${id}/complete`); toast.success("Marked complete"); load(); }
    catch { toast.error("Failed"); }
  };

  const toggleExpand = async (id) => {
    if (expanded === id) { setExpanded(null); return; }
    try { await api.get(`/briefings/${id}`); } catch { /* ignore */ }
    setExpanded(id);
  };

  const myTracking = (b) => (b.tracking || {})[user?.id];
  const priorityColor = { urgent: "text-red-600 bg-red-50", high: "text-orange-600 bg-orange-50", normal: "text-[#0F172A] bg-[#F1F5F9]", low: "text-[#6B7280] bg-[#F9FAFB]" };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-[#C5A059] border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div data-testid="briefings-page" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-[#0F172A]">Team Briefings</h1>
          <p className="text-sm text-[#6B7280]">Communication and action items from Team Leaders</p>
        </div>
        {isLeader && (
          <Button onClick={() => setShowCreate(!showCreate)} className="bg-[#0F172A] gap-1.5" data-testid="create-briefing-btn">
            <Megaphone size={14} /> New Briefing
          </Button>
        )}
      </div>

      {/* Create Form */}
      {showCreate && isLeader && (
        <div className="bg-white rounded-xl border border-[#E5E7EB] p-6 space-y-4" data-testid="create-briefing-form">
          <Input placeholder="Subject" value={form.subject} onChange={e => setForm({ ...form, subject: e.target.value })} data-testid="briefing-subject" />
          <textarea
            className="w-full border border-[#D1D5DB] rounded-lg px-3 py-2 text-sm min-h-[100px] focus:border-[#C5A059] focus:ring-1 focus:ring-[#C5A059] outline-none"
            placeholder="Briefing message..."
            value={form.message}
            onChange={e => setForm({ ...form, message: e.target.value })}
            data-testid="briefing-message"
          />
          <div className="grid grid-cols-3 gap-3">
            <select className="border border-[#D1D5DB] rounded-lg px-3 py-2 text-sm" value={form.team_id} onChange={e => setForm({ ...form, team_id: e.target.value })} data-testid="briefing-team">
              <option value="">All my teams</option>
              {teams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
            <select className="border border-[#D1D5DB] rounded-lg px-3 py-2 text-sm" value={form.priority} onChange={e => setForm({ ...form, priority: e.target.value })} data-testid="briefing-priority">
              <option value="low">Low</option>
              <option value="normal">Normal</option>
              <option value="high">High</option>
              <option value="urgent">Urgent</option>
            </select>
            <Input type="date" placeholder="Deadline (optional)" value={form.deadline} onChange={e => setForm({ ...form, deadline: e.target.value })} data-testid="briefing-deadline" />
          </div>
          <div className="flex gap-2">
            <Button onClick={handleCreate} className="bg-[#C5A059] text-[#0F172A] gap-1" data-testid="send-briefing-btn">
              <PaperPlaneTilt size={14} weight="fill" /> Send Briefing
            </Button>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
          </div>
        </div>
      )}

      {/* Briefings List */}
      <div className="space-y-3">
        {briefings.length === 0 ? (
          <div className="bg-white rounded-xl border border-[#E5E7EB] p-8 text-center text-[#9CA3AF] text-sm">No briefings yet</div>
        ) : briefings.map(b => {
          const mt = myTracking(b);
          const isExpanded = expanded === b.id;
          return (
            <div key={b.id} className="bg-white rounded-xl border border-[#E5E7EB] overflow-hidden" data-testid={`briefing-${b.id}`}>
              <button onClick={() => toggleExpand(b.id)} className="w-full px-5 py-4 flex items-center gap-3 text-left hover:bg-[#F9FAFB] transition">
                <Megaphone size={20} className="text-[#C5A059] shrink-0" weight="duotone" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className="text-sm font-bold text-[#0F172A] truncate">{b.subject}</span>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded font-semibold ${priorityColor[b.priority] || priorityColor.normal}`}>{b.priority}</span>
                  </div>
                  <p className="text-xs text-[#6B7280]">
                    From {b.created_by_name} &middot; {new Date(b.created_at).toLocaleDateString("en-GB")}
                    {b.deadline && <span> &middot; Due {b.deadline}</span>}
                  </p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {mt?.status === "completed" && <CheckCircle size={16} className="text-green-600" weight="fill" />}
                  {mt?.status === "acknowledged" && <Eye size={16} className="text-blue-600" />}
                  {mt?.status === "delivered" && <Clock size={14} className="text-[#9CA3AF]" />}
                  {isExpanded ? <CaretUp size={14} className="text-[#6B7280]" /> : <CaretDown size={14} className="text-[#6B7280]" />}
                </div>
              </button>

              {isExpanded && (
                <div className="px-5 pb-4 border-t border-[#F3F4F6]">
                  <p className="text-sm text-[#374151] whitespace-pre-wrap my-3">{b.message}</p>
                  {!isLeader && mt?.status !== "completed" && (
                    <div className="flex gap-2 mt-3">
                      {mt?.status !== "acknowledged" && mt?.status !== "completed" && (
                        <Button size="sm" variant="outline" onClick={() => acknowledge(b.id)} className="gap-1" data-testid={`ack-${b.id}`}>
                          <Eye size={12} /> Acknowledge
                        </Button>
                      )}
                      <Button size="sm" onClick={() => complete(b.id)} className="bg-[#10B981] gap-1" data-testid={`complete-${b.id}`}>
                        <CheckCircle size={12} weight="fill" /> Mark Complete
                      </Button>
                    </div>
                  )}
                  {/* TL tracking view */}
                  {isLeader && b.tracking && typeof b.tracking === "object" && Object.keys(b.tracking).length > 0 && (
                    <div className="mt-3 border-t border-[#F3F4F6] pt-3">
                      <p className="text-xs font-semibold text-[#6B7280] mb-2 uppercase">Tracking</p>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                        {Object.values(b.tracking).map((t, i) => (
                          <div key={i} className="bg-[#F9FAFB] rounded px-2 py-1.5 flex items-center gap-1.5">
                            {t.status === "completed" ? <CheckCircle size={12} className="text-green-600" weight="fill" /> :
                             t.status === "acknowledged" ? <Eye size={12} className="text-blue-600" /> :
                             t.status === "opened" ? <Eye size={12} className="text-[#C5A059]" /> :
                             <Clock size={12} className="text-[#9CA3AF]" />}
                            <span className="truncate">{t.user_id?.slice(-6) || "User"}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
